import {
  acknowledgeSyncCommand,
  fetchWorkspaceSnapshot,
  reportSyncTelemetry,
  syncOutboundOperationsBatch,
} from "@/lib/shopApi";
import { getLocalizationSnapshot } from "@/lib/localization";
import { notifySubscriptionReadOnly } from "@/lib/apiClient";
import { isSubscriptionReadOnly } from "@/lib/subscriptionFlow";
import { runWebSyncCommand } from "./webSyncCommands";
import { sanitizeWebSyncPayload } from "./webSyncPayloads";

const DB_NAME = "shopbook.web.offline";
const DB_VERSION = 2;
const STORE_DEFINITIONS = {
  products: { keyPath: "key" },
  sales: { keyPath: "key" },
  salesArchive: { keyPath: "key" },
  debts: { keyPath: "key" },
  debtPayments: { keyPath: "key" },
  stockMovements: { keyPath: "key" },
  syncOperations: { keyPath: "key" },
  metadata: { keyPath: "key" },
};
const STORES = Object.keys(STORE_DEFINITIONS);
const BATCH_SIZE = 25;
const PROCESSING_TIMEOUT_MS = 2 * 60_000;
const SYNC_LOCK_TTL_MS = 90_000;
const SYNC_CLIENT_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const TELEMETRY_MAX_INTERVAL_MS = 60_000;
const DAY_MS = 24 * 60 * 60_000;
let lastTelemetryAt = 0;
let lastTelemetrySignature = "";
const SYNC_ERROR_CODES = {
  indexedDbOpenFailed: "INDEXED_DB_OPEN_FAILED",
  indexedDbRequestFailed: "INDEXED_DB_REQUEST_FAILED",
  indexedDbTransactionAborted: "INDEXED_DB_TRANSACTION_ABORTED",
  indexedDbTransactionFailed: "INDEXED_DB_TRANSACTION_FAILED",
  recoveredInterruptedProcessingBatch: "RECOVERED_INTERRUPTED_PROCESSING_BATCH",
  waitInitialStockBeforeSale: "WAIT_INITIAL_STOCK_BEFORE_SALE",
  waitProductBeforeSale: "WAIT_PRODUCT_BEFORE_SALE",
  waitProductBeforeStockMovement: "WAIT_PRODUCT_BEFORE_STOCK_MOVEMENT",
  waitSaleBeforeStockMovement: "WAIT_SALE_BEFORE_STOCK_MOVEMENT",
};
const LEGACY_SYNC_ERRORS = {
  [SYNC_ERROR_CODES.waitInitialStockBeforeSale]: "Waiting for initial stock sync before sale",
  [SYNC_ERROR_CODES.waitProductBeforeSale]: "Waiting for product sync before sale",
  [SYNC_ERROR_CODES.waitProductBeforeStockMovement]: "Waiting for product sync before stock movement",
  [SYNC_ERROR_CODES.waitSaleBeforeStockMovement]: "Waiting for sale sync before stock movement",
  [SYNC_ERROR_CODES.recoveredInterruptedProcessingBatch]: "Recovered interrupted processing batch",
};
const RECOVERABLE_SYNC_ERRORS = new Set([
  "Network request failed",
  "HTTP 500: could not sync sale",
  "HTTP 500: could not sync stock movement",
  SYNC_ERROR_CODES.waitInitialStockBeforeSale,
  SYNC_ERROR_CODES.waitProductBeforeSale,
  SYNC_ERROR_CODES.waitProductBeforeStockMovement,
  SYNC_ERROR_CODES.waitSaleBeforeStockMovement,
  LEGACY_SYNC_ERRORS[SYNC_ERROR_CODES.waitInitialStockBeforeSale],
  LEGACY_SYNC_ERRORS[SYNC_ERROR_CODES.waitProductBeforeSale],
  LEGACY_SYNC_ERRORS[SYNC_ERROR_CODES.waitProductBeforeStockMovement],
  LEGACY_SYNC_ERRORS[SYNC_ERROR_CODES.waitSaleBeforeStockMovement],
]);

const SUBSCRIPTION_READ_ONLY_MESSAGE =
  "Your subscription is read-only. Renew it to create, update, or delete records.";

function assertSubscriptionWritable(session) {
  if (isSubscriptionReadOnly(session?.subscription)) {
    notifySubscriptionReadOnly();
    throw new Error(SUBSCRIPTION_READ_ONLY_MESSAGE);
  }
}

function getSettingsCopy() {
  return getLocalizationSnapshot().messages.settings;
}

function translateSyncError(error) {
  if (!error) return error;

  const copy = getSettingsCopy();
  const mapped = {
    [SYNC_ERROR_CODES.indexedDbOpenFailed]: copy.indexedDbOpenFailed,
    [SYNC_ERROR_CODES.indexedDbRequestFailed]: copy.indexedDbRequestFailed,
    [SYNC_ERROR_CODES.indexedDbTransactionAborted]: copy.indexedDbTransactionAborted,
    [SYNC_ERROR_CODES.indexedDbTransactionFailed]: copy.indexedDbTransactionFailed,
    [SYNC_ERROR_CODES.recoveredInterruptedProcessingBatch]: copy.syncRecoveredInterrupted,
    [SYNC_ERROR_CODES.waitInitialStockBeforeSale]: copy.syncWaitInitialStockBeforeSale,
    [SYNC_ERROR_CODES.waitProductBeforeSale]: copy.syncWaitProductBeforeSale,
    [SYNC_ERROR_CODES.waitProductBeforeStockMovement]: copy.syncWaitProductBeforeStockMovement,
    [SYNC_ERROR_CODES.waitSaleBeforeStockMovement]: copy.syncWaitSaleBeforeStockMovement,
  };

  const legacyMatch = Object.entries(LEGACY_SYNC_ERRORS).find(([, value]) => value === error)?.[0];
  return mapped[error] ?? mapped[legacyMatch] ?? error;
}

function nowIso() {
  return new Date().toISOString();
}

function getBackoffDate(attempts) {
  const baseSeconds = Math.min(600, 2 ** Math.max(1, attempts) * 5);
  const jitterSeconds = Math.floor(Math.random() * Math.min(30, baseSeconds));
  return new Date(Date.now() + (baseSeconds + jitterSeconds) * 1000).toISOString();
}

function getDeferredRetryDate(nextAttemptAt) {
  const parsed = nextAttemptAt ? Date.parse(nextAttemptAt) : Number.NaN;
  if (!Number.isNaN(parsed) && parsed > Date.now()) {
    return nextAttemptAt;
  }
  return new Date(Date.now() + 1000).toISOString();
}

function isDue(operation, at = Date.now()) {
  if (operation.status === "QUEUED") return true;
  if (operation.status !== "FAILED") return false;
  if (!operation.nextAttemptAt) return true;
  const nextAttemptAt = Date.parse(operation.nextAttemptAt);
  return Number.isNaN(nextAttemptAt) || nextAttemptAt <= at;
}

function isWaitingOperation(operation) {
  return operation.status === "FAILED" && RECOVERABLE_SYNC_ERRORS.has(operation.lastError);
}

export function getWebShopKey(session) {
  const stableShopId = String(session?.shopId || "").trim().toLowerCase();
  const stableBranchId = String(session?.selectedBranchId || "").trim().toLowerCase();
  if (!stableBranchId) {
    throw new Error("A selected branch is required before local shop data can be opened.");
  }
  if (stableShopId) return `shop-${stableShopId}:branch-${stableBranchId}`;
  const fallback = `${session?.shopName || "shop"}:${session?.phone || session?.sessionId || "session"}`;
  return `${fallback.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}:branch-${stableBranchId}`;
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error(SYNC_ERROR_CODES.indexedDbRequestFailed));
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error(SYNC_ERROR_CODES.indexedDbTransactionFailed));
    tx.onabort = () => reject(tx.error || new Error(SYNC_ERROR_CODES.indexedDbTransactionAborted));
  });
}

let dbPromise = null;
let activeDb = null;

function applyDatabaseUpgrade(db) {
  for (const [storeName, definition] of Object.entries(STORE_DEFINITIONS)) {
    if (!db.objectStoreNames.contains(storeName)) {
      db.createObjectStore(storeName, definition);
    }
  }
}

function resetDatabaseConnection(db = null) {
  if (!db || activeDb === db) {
    activeDb = null;
    dbPromise = null;
  }
}

function bindDatabaseLifecycle(db) {
  db.onversionchange = () => {
    db.close();
    resetDatabaseConnection(db);
  };
  db.onclose = () => {
    resetDatabaseConnection(db);
  };
}

function openDatabase() {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error(getSettingsCopy().indexedDbUnavailable));
  }

  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => {
      resetDatabaseConnection();
      reject(request.error || new Error(SYNC_ERROR_CODES.indexedDbOpenFailed));
    };
    request.onblocked = () => {
      resetDatabaseConnection();
    };
    request.onupgradeneeded = () => {
      applyDatabaseUpgrade(request.result);
    };
    request.onsuccess = () => {
      const db = request.result;
      activeDb = db;
      bindDatabaseLifecycle(db);
      resolve(db);
    };
  });

  return dbPromise;
}

function shouldRetryDatabaseOperation(error) {
  const message = String(error?.message || "").toLowerCase();
  return (
    error?.name === "InvalidStateError" ||
    error?.name === "AbortError" ||
    message.includes("database connection is closing") ||
    message.includes("connection is closing") ||
    message.includes("object store was not found") ||
    message.includes("transaction is not active")
  );
}

async function runWithDatabaseRetry(operation) {
  try {
    return await operation();
  } catch (error) {
    if (!shouldRetryDatabaseOperation(error)) {
      throw error;
    }
    if (activeDb) {
      try {
        activeDb.close();
      } catch {
        // Best effort close before reopening a fresh connection.
      }
    }
    resetDatabaseConnection(activeDb);
    return operation();
  }
}

function withStore(storeName, mode, callback) {
  return runWithDatabaseRetry(() => openDatabase().then(async (db) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const result = await callback(store);
    await txDone(tx);
    return result;
  }));
}

function withStores(storeNames, mode, callback) {
  return runWithDatabaseRetry(() => openDatabase().then(async (db) => {
    const tx = db.transaction(storeNames, mode);
    const stores = Object.fromEntries(storeNames.map((name) => [name, tx.objectStore(name)]));
    const result = await callback(stores);
    await txDone(tx);
    return result;
  }));
}

async function getAll(storeName) {
  return withStore(storeName, "readonly", (store) => requestToPromise(store.getAll()));
}

function entityKey(shopKey, id) {
  return `${shopKey}:${id}`;
}

function forShop(shopKey, rows) {
  return rows.filter((row) => row.shopKey === shopKey).map(({ key, shopKey: _shopKey, ...record }) => record);
}

function getBrowserStorage() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const probeKey = `${DB_NAME}:storage-probe`;
    window.localStorage.setItem(probeKey, "1");
    window.localStorage.removeItem(probeKey);
    return window.localStorage;
  } catch {
    return null;
  }
}

function getSyncLockKey(shopKey) {
  return `${DB_NAME}:sync-lock:${shopKey}`;
}

function readSyncLock(storage, shopKey) {
  try {
    const parsed = JSON.parse(storage.getItem(getSyncLockKey(shopKey)) || "null");
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function acquireSyncLock(shopKey) {
  const storage = getBrowserStorage();
  if (!storage) return { acquired: true, shopKey };

  const existing = readSyncLock(storage, shopKey);
  if (existing?.ownerId && existing.ownerId !== SYNC_CLIENT_ID && Number(existing.expiresAt || 0) > Date.now()) {
    return { acquired: false, shopKey };
  }

  const lock = {
    expiresAt: Date.now() + SYNC_LOCK_TTL_MS,
    ownerId: SYNC_CLIENT_ID,
    shopKey,
  };

  try {
    storage.setItem(getSyncLockKey(shopKey), JSON.stringify(lock));
    const confirmed = readSyncLock(storage, shopKey);
    return { acquired: confirmed?.ownerId === SYNC_CLIENT_ID, shopKey };
  } catch {
    return { acquired: true, shopKey };
  }
}

function releaseSyncLock(lock) {
  if (!lock?.acquired) return;
  const storage = getBrowserStorage();
  if (!storage) return;

  const existing = readSyncLock(storage, lock.shopKey);
  if (existing?.ownerId === SYNC_CLIENT_ID) {
    storage.removeItem(getSyncLockKey(lock.shopKey));
  }
}

async function setMetadata(shopKey, id, value) {
  await withStore("metadata", "readwrite", (store) => {
    store.put({ key: entityKey(shopKey, id), shopKey, id, value });
  });
}

async function getMetadata(shopKey, id) {
  return withStore("metadata", "readonly", (store) => requestToPromise(store.get(entityKey(shopKey, id))));
}

async function prepareWebWorkspaceForAuthGeneration(session) {
  const shopKey = getWebShopKey(session);
  const authGeneration = Number(session?.authGeneration || 0);
  const ownerUserId = String(session?.userId || "").trim();
  if (authGeneration <= 0 || !ownerUserId) return;

  const stored = await getMetadata(shopKey, "authGeneration");
  const storedOwner = await getMetadata(shopKey, "ownerUserId");
  const storedGeneration = Number(stored?.value || 0);
  if (storedGeneration === authGeneration && storedOwner?.value === ownerUserId) return;
  const existingOperations = (await getAll("syncOperations"))
    .filter((operation) =>
      operation.shopKey === shopKey &&
      operation.status !== "SYNCED" &&
      (Number(operation.authGeneration || 0) !== authGeneration ||
        operation.ownerUserId !== ownerUserId ||
        operation.branchId !== session.selectedBranchId)
    );
  const requiresRebuild = storedGeneration > 0 || Boolean(storedOwner?.value) || existingOperations.length > 0;

  const workspaceStores = ["products", "sales", "debts", "debtPayments", "stockMovements"];
  await withStores([...workspaceStores, "syncOperations", "metadata"], "readwrite", async (stores) => {
    if (requiresRebuild) {
      const [workspaceRecords, operations] = await Promise.all([
        Promise.all(workspaceStores.map((storeName) => requestToPromise(stores[storeName].getAll()))),
        requestToPromise(stores.syncOperations.getAll()),
      ]);
      workspaceStores.forEach((storeName, index) => {
        const records = workspaceRecords[index];
        records
          .filter((record) => record.shopKey === shopKey)
          .forEach((record) => stores[storeName].delete(record.key));
      });
      operations
        .filter((operation) =>
          operation.shopKey === shopKey &&
          operation.status !== "SYNCED" &&
          (Number(operation.authGeneration || 0) !== authGeneration ||
            operation.ownerUserId !== ownerUserId ||
            operation.branchId !== session.selectedBranchId)
        )
        .forEach((operation) => {
          stores.syncOperations.put({
            ...operation,
            lastError: "Created under a replaced login session; review is required.",
            nextAttemptAt: null,
            status: "QUARANTINED",
            updatedAt: nowIso(),
          });
        });
      stores.metadata.delete(entityKey(shopKey, "workspacePullCursor"));
      stores.metadata.delete(entityKey(shopKey, "lastSyncedAt"));
    }
    stores.metadata.put({
      id: "authGeneration",
      key: entityKey(shopKey, "authGeneration"),
      shopKey,
      value: authGeneration,
    });
    stores.metadata.put({
      id: "ownerUserId",
      key: entityKey(shopKey, "ownerUserId"),
      shopKey,
      value: ownerUserId,
    });
  });
}

export async function seedWebOfflineWorkspace(session, workspace) {
  if (!session) return;

  const shopKey = getWebShopKey(session);
  const products = workspace.products ?? [];
  const sales = workspace.sales ?? [];
  const debts = workspace.debts ?? [];
  const debtPayments = workspace.debtPayments ?? [];
  const inventoryAllocations = workspace.inventoryAllocations ?? [];
  const stockMovements = workspace.stockMovements ?? [];

  await withStores(["products", "sales", "debts", "debtPayments", "stockMovements", "metadata"], "readwrite", async (stores) => {
    products.forEach((product) => stores.products.put({ ...product, key: entityKey(shopKey, product.id), shopKey }));
    sales.forEach((sale) => stores.sales.put({
      ...sale,
      key: entityKey(shopKey, sale.id),
      shopKey,
      syncState: sale.syncState || "Synced",
    }));
    debts.forEach((debt) => stores.debts.put({ ...debt, key: entityKey(shopKey, debt.id), shopKey }));
    debtPayments.forEach((payment) => stores.debtPayments.put({ ...payment, key: entityKey(shopKey, payment.id), shopKey }));
    stockMovements.forEach((movement) => stores.stockMovements.put({ ...movement, key: entityKey(shopKey, movement.id || movement.movementId), shopKey }));
    stores.metadata.put({ key: entityKey(shopKey, "inventoryAllocations"), shopKey, id: "inventoryAllocations", value: inventoryAllocations });
    stores.metadata.put({ key: entityKey(shopKey, "lastSyncedAt"), shopKey, id: "lastSyncedAt", value: nowIso() });
  });
}

export async function loadWebOfflineWorkspace(session) {
  if (!session) return null;

  await prepareWebWorkspaceForAuthGeneration(session);
  const shopKey = getWebShopKey(session);
  const [products, sales, debts, debtPayments, stockMovements, operations, lastSynced, inventoryAllocations] = await Promise.all([
    getAll("products"),
    getAll("sales"),
    getAll("debts"),
    getAll("debtPayments"),
    getAll("stockMovements"),
    getAll("syncOperations"),
    getMetadata(shopKey, "lastSyncedAt").catch(() => null),
    getMetadata(shopKey, "inventoryAllocations").catch(() => null),
  ]);
  const shopOperations = operations.filter(
    (operation) =>
      operation.shopKey === shopKey &&
      operation.branchId === session.selectedBranchId &&
      operation.ownerUserId === session.userId,
  );
  const queuedCount = shopOperations.filter((operation) => operation.status === "QUEUED" || operation.status === "PROCESSING").length;
  const failedOperations = shopOperations.filter((operation) => operation.status === "FAILED");
  const nextRetryAt = failedOperations
    .map((operation) => operation.nextAttemptAt)
    .filter(Boolean)
    .sort()[0] ?? null;

  return {
    debtPayments: forShop(shopKey, debtPayments).sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || ""))),
    debts: forShop(shopKey, debts),
    inventoryAllocations: Array.isArray(inventoryAllocations?.value) ? inventoryAllocations.value : [],
    products: forShop(shopKey, products).filter((product) => product.status !== "Archived"),
    sales: forShop(shopKey, sales).sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || ""))),
    stockMovements: forShop(shopKey, stockMovements),
    sync: {
      failedCount: failedOperations.length,
      isProcessing: shopOperations.some((operation) => operation.status === "PROCESSING"),
      lastError: translateSyncError(failedOperations[0]?.lastError ?? null),
      lastSyncedAt: lastSynced?.value ?? null,
      nextRetryAt,
      online: typeof navigator === "undefined" ? true : navigator.onLine,
      queuedCount,
    },
  };
}

function summarizeQueueItem(operation, productById = new Map()) {
  const { messages } = getLocalizationSnapshot();
  const copy = messages.settings;
  const payload = operation.payload ?? {};
  const base = {
    aggregateId: operation.aggregateId,
    attempts: Number(operation.attempts || 0),
    createdAt: operation.createdAt,
    id: operation.id,
    idempotencyKey: operation.idempotencyKey,
    lastError: translateSyncError(operation.lastError ?? null),
    nextAttemptAt: operation.nextAttemptAt ?? null,
    operationType: operation.operationType,
    status: isWaitingOperation(operation) ? "WAITING" : operation.status,
    updatedAt: operation.updatedAt,
  };

  if (operation.operationType === "PRODUCT_UPSERT") {
    return {
      ...base,
      canDelete: false,
      deleteBlockedReason: copy.queueProductChangeBlocked,
      detail: payload.status === "Archived" ? copy.queueProductArchivePending : copy.queueProductUpdatePending,
      itemLabel: payload.name || copy.operationProduct,
      quantityDelta: null,
    };
  }

  if (operation.operationType === "STOCK_MOVEMENT_RECORDED") {
    const product = productById.get(payload.productId);
    const isStandalone = !payload.saleId;
    return {
      ...base,
      canDelete: isStandalone && operation.status !== "PROCESSING",
      deleteBlockedReason: isStandalone
        ? operation.status === "PROCESSING"
          ? copy.queueStockDeleteWait
          : null
        : copy.queueStockSaleLinked,
      detail: payload.type === "IN" ? copy.queueStockAdditionPending : copy.queueStockReductionPending,
      itemLabel: product?.name || payload.productId || copy.queueInventoryItem,
      quantityDelta: payload.type === "IN" ? payload.quantity : -payload.quantity,
    };
  }

  if (operation.operationType === "SALE_RECORDED") {
    const names = (payload.items ?? []).map((item) => item.name).filter(Boolean);
    return {
      ...base,
      canDelete: false,
      deleteBlockedReason: copy.queueSaleDeleteBlocked,
      detail: copy.queueSaleItems(payload.items?.length ?? 0),
      itemLabel: names.length ? names.join(", ") : copy.queuePendingSale,
      quantityDelta: null,
    };
  }

  if (operation.operationType === "DEBT_RECORDED") {
    return {
      ...base,
      canDelete: false,
      deleteBlockedReason: copy.queueDebtDeleteBlocked,
      detail: payload.customerName || copy.queueCustomerCredit,
      itemLabel: payload.itemSummary || copy.queuePendingDebtRecord,
      quantityDelta: null,
    };
  }

  if (operation.operationType === "DEBT_PAYMENT_RECORDED") {
    return {
      ...base,
      canDelete: false,
      deleteBlockedReason: copy.queueDebtPaymentDeleteBlocked,
      detail: payload.customerName || copy.queueCustomerPayment,
      itemLabel: copy.queuePendingDebtPayment,
      quantityDelta: payload.amount ?? null,
    };
  }

  return {
    ...base,
    canDelete: false,
    deleteBlockedReason: copy.queueGenericDeleteBlocked,
    detail: operation.operationType,
    itemLabel: copy.queueGenericRecord,
    quantityDelta: null,
  };
}

export async function getWebPendingSyncQueueItems(session) {
  if (!session) return [];

  const shopKey = getWebShopKey(session);
  const [operations, products] = await Promise.all([getAll("syncOperations"), getAll("products")]);
  const productById = new Map(
    products
      .filter((product) => product.shopKey === shopKey)
      .map((product) => [product.id, product])
  );

  return operations
    .filter((operation) =>
      operation.shopKey === shopKey &&
      operation.branchId === session.selectedBranchId &&
      operation.ownerUserId === session.userId &&
      ["QUEUED", "PROCESSING", "FAILED", "QUARANTINED"].includes(operation.status))
    .sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)))
    .map((operation) => summarizeQueueItem(operation, productById));
}

export async function retryFailedWebSyncOperations(session) {
  if (!session) return;

  const shopKey = getWebShopKey(session);
  await withStore("syncOperations", "readwrite", async (store) => {
    const operations = await requestToPromise(store.getAll());
    operations
      .filter((operation) =>
        operation.shopKey === shopKey &&
        operation.branchId === session.selectedBranchId &&
        operation.ownerUserId === session.userId &&
        ["FAILED", "QUARANTINED"].includes(operation.status))
      .forEach((operation) => {
        store.put({
          ...operation,
          authGeneration: Number(session.authGeneration || 0),
          lastError: null,
          status: "QUEUED",
          updatedAt: nowIso(),
        });
      });
  });
}

export async function deleteWebPendingSyncQueueItem(session, operationId) {
  if (!session || !operationId) return;

  const shopKey = getWebShopKey(session);
  await withStores(["syncOperations", "stockMovements", "products"], "readwrite", async (stores) => {
    const { messages } = getLocalizationSnapshot();
    const copy = messages.settings;
    const operations = await requestToPromise(stores.syncOperations.getAll());
    const operation = operations.find(
      (item) =>
        item.shopKey === shopKey &&
        item.branchId === session.selectedBranchId &&
        item.ownerUserId === session.userId &&
        item.id === operationId,
    );
    if (!operation) {
      throw new Error(copy.queuedRecordUnavailable);
    }
    if (operation.status === "PROCESSING") {
      throw new Error(copy.queuedDeleteWait);
    }
    if (operation.operationType !== "STOCK_MOVEMENT_RECORDED" || operation.payload?.saleId) {
      throw new Error(copy.queuedDeleteStandaloneOnly);
    }

    const payload = operation.payload;
    const movementId = payload.movementId || operation.aggregateId;
    stores.syncOperations.delete(operation.key);
    stores.stockMovements.delete(entityKey(shopKey, movementId));

    const productKey = entityKey(shopKey, payload.productId);
    const product = await requestToPromise(stores.products.get(productKey));
    if (product && product.stock !== null && product.stock !== undefined) {
      const delta = payload.type === "IN" ? -Number(payload.quantity || 0) : Number(payload.quantity || 0);
      stores.products.put({ ...product, stock: Math.max(0, Number(product.stock || 0) + delta) });
    }
  });
}

export async function enqueueWebSyncOperation(session, { aggregateId, id, idempotencyKey, operationType, payload }) {
  assertSubscriptionWritable(session);
  const shopKey = getWebShopKey(session);
  const authGeneration = Number(session?.authGeneration || 0);
  const branchId = String(session?.selectedBranchId || "").trim();
  const ownerUserId = String(session?.userId || "").trim();
  if (authGeneration <= 0) {
    throw new Error("A current authenticated session is required before offline work can be queued.");
  }
  if (!branchId || !ownerUserId) {
    throw new Error("The authenticated user and selected branch are required before offline work can be queued.");
  }
  const timestamp = nowIso();
  await withStore("syncOperations", "readwrite", async (store) => {
    const key = entityKey(shopKey, idempotencyKey);
    const existing = await requestToPromise(store.get(key));
    if (
      existing &&
      (Number(existing.authGeneration || 0) !== authGeneration ||
        existing.ownerUserId !== ownerUserId ||
        existing.branchId !== branchId) &&
      existing.status !== "SYNCED"
    ) {
      store.put({
        ...existing,
        key: `${key}:quarantined:g${Number(existing.authGeneration || 0)}`,
        lastError: "Created under a replaced login session; review is required.",
        status: "QUARANTINED",
        updatedAt: timestamp,
      });
    }
    store.put({
      aggregateId,
      attempts: existing?.attempts ?? 0,
      authGeneration,
      branchId,
      createdAt: existing?.createdAt ?? timestamp,
      id,
      idempotencyKey,
      key,
      lastError: null,
      operationType,
      ownerUserId,
      payload,
      shopKey,
      status: existing?.status === "SYNCED" ? "SYNCED" : "QUEUED",
      nextAttemptAt: null,
      updatedAt: timestamp,
    });
  });
}

export async function commitWebSaleBundle(
  session,
  { debt, debtIdempotencyKey, sale, saleIdempotencyKey, stockMovements },
) {
  assertSubscriptionWritable(session);
  const shopKey = getWebShopKey(session);
  const authGeneration = Number(session?.authGeneration || 0);
  const branchId = String(session?.selectedBranchId || "").trim();
  const ownerUserId = String(session?.userId || "").trim();
  if (authGeneration <= 0 || !branchId || !ownerUserId) {
    throw new Error("A current authenticated user and branch are required to record a sale.");
  }
  if (debt && (debt.saleId !== sale.id || debt.branchId !== branchId)) {
    throw new Error("Credit-sale debt identity does not match the current sale and branch.");
  }
  if (!['online', 'offline'].includes(sale.inventoryExecutionMode)) {
    throw new Error("A sale inventory execution mode is required.");
  }
  if (sale.inventoryExecutionMode === "offline") {
    const referencedProducts = new Set((sale.offlineInventoryGrants || []).map((grant) => grant.productId));
    const trackedProducts = new Set(stockMovements.map((movement) => movement.productId));
    for (const productId of trackedProducts) {
      if (!referencedProducts.has(productId)) {
        throw new Error("Every offline tracked product requires a current device inventory grant.");
      }
    }
  }
  const timestamp = nowIso();
  await withStores(
    ["products", "sales", "debts", "stockMovements", "syncOperations", "metadata"],
    "readwrite",
    async (stores) => {
      stores.sales.put({
        ...sale,
        key: entityKey(shopKey, sale.id),
        shopKey,
      });
      for (const movement of stockMovements) {
        const movementId = movement.id || movement.movementId;
        stores.stockMovements.put({
          ...movement,
          id: movementId,
          key: entityKey(shopKey, movementId),
          shopKey,
        });
        const productKey = entityKey(shopKey, movement.productId);
        const product = await requestToPromise(stores.products.get(productKey));
        if (product && product.stock !== null && product.stock !== undefined) {
          const nextStock = Math.max(0, Number(product.stock || 0) - Number(movement.quantity || 0));
          stores.products.put({ ...product, stock: nextStock });
        }
      }
      if (sale.inventoryExecutionMode === "offline" || sale.inventoryExecutionMode === "online") {
        const allocationKey = entityKey(shopKey, "inventoryAllocations");
        const allocationRecord = await requestToPromise(stores.metadata.get(allocationKey));
        if (Array.isArray(allocationRecord?.value)) {
          const quantityByProduct = stockMovements.reduce((quantities, movement) => ({
            ...quantities,
            [movement.productId]: (quantities[movement.productId] || 0) + Number(movement.quantity || 0),
          }), {});
          stores.metadata.put({
            ...allocationRecord,
            value: allocationRecord.value.map((allocation) => {
              const soldQuantity = quantityByProduct[allocation.productId] || 0;
              if (!soldQuantity || allocation.userId !== ownerUserId) return allocation;
              return {
                ...allocation,
                allocatedQuantity: Math.max(0, Number(allocation.allocatedQuantity || 0) - soldQuantity),
                updatedAt: timestamp,
              };
            }),
          });
        }
      }
      const putOperation = async ({ aggregateId, id, idempotencyKey, operationType, payload }) => {
        const key = entityKey(shopKey, idempotencyKey);
        const existing = await requestToPromise(stores.syncOperations.get(key));
        if (
          existing &&
          (existing.aggregateId !== aggregateId ||
            existing.branchId !== branchId ||
            existing.ownerUserId !== ownerUserId ||
            existing.operationType !== operationType)
        ) {
          throw new Error(`Idempotency key ${idempotencyKey} is already bound to another operation.`);
        }
        stores.syncOperations.put({
          aggregateId,
          attempts: existing?.attempts ?? 0,
          authGeneration,
          branchId,
          createdAt: existing?.createdAt ?? timestamp,
          id,
          idempotencyKey,
          key,
          lastError: null,
          nextAttemptAt: null,
          operationType,
          ownerUserId,
          payload,
          shopKey,
          status: existing?.status === "SYNCED" ? "SYNCED" : "QUEUED",
          updatedAt: timestamp,
        });
      };
      await putOperation({
        aggregateId: sale.id,
        id: `sync-sale-${sale.id}`,
        idempotencyKey: saleIdempotencyKey,
        operationType: "SALE_RECORDED",
        payload: sanitizeWebSyncPayload("SALE_RECORDED", sale),
      });
      if (debt) {
        stores.debts.put({ ...debt, key: entityKey(shopKey, debt.id), shopKey });
        await putOperation({
          aggregateId: debt.id,
          id: `sync-debt-${debt.id}`,
          idempotencyKey: debtIdempotencyKey,
          operationType: "DEBT_RECORDED",
          payload: debt,
        });
      }
      stores.metadata.delete(entityKey(shopKey, "activeCart"));
    },
  );
}

export async function loadWebCart(session) {
  if (!session) return [];
  await prepareWebWorkspaceForAuthGeneration(session);
  const stored = await getMetadata(getWebShopKey(session), "activeCart");
  return Array.isArray(stored?.value) ? stored.value : [];
}

export async function saveWebCart(session, cart) {
  if (!session) return;
  const shopKey = getWebShopKey(session);
  if (!Array.isArray(cart) || cart.length === 0) {
    await withStore("metadata", "readwrite", (store) => {
      store.delete(entityKey(shopKey, "activeCart"));
    });
    return;
  }
  await setMetadata(shopKey, "activeCart", cart);
}

export async function loadWebGlobalCatalog(session) {
  if (!session) return [];
  const stored = await getMetadata(getWebShopKey(session), "globalProductCatalog");
  return Array.isArray(stored?.value) ? stored.value : [];
}

export async function saveWebGlobalCatalog(session, products) {
  if (!session || !Array.isArray(products)) return;
  await setMetadata(getWebShopKey(session), "globalProductCatalog", products);
}

export async function saveWebProduct(session, product) {
  const shopKey = getWebShopKey(session);
  await withStore("products", "readwrite", (store) => {
    store.put({ ...product, key: entityKey(shopKey, product.id), shopKey });
  });
}

export async function saveWebProducts(session, products) {
  if (!session || !Array.isArray(products) || !products.length) {
    return;
  }

  const shopKey = getWebShopKey(session);
  await withStore("products", "readwrite", (store) => {
    products.forEach((product) => {
      store.put({ ...product, key: entityKey(shopKey, product.id), shopKey });
    });
  });
}

export async function saveWebSale(session, sale) {
  const shopKey = getWebShopKey(session);
  await withStore("sales", "readwrite", (store) => {
    store.put({ ...sale, key: entityKey(shopKey, sale.id), shopKey });
  });
}

export async function saveWebDebt(session, debt) {
  const shopKey = getWebShopKey(session);
  await withStore("debts", "readwrite", (store) => {
    store.put({ ...debt, key: entityKey(shopKey, debt.id), shopKey });
  });
}

export async function saveWebDebtPayment(session, payment) {
  const shopKey = getWebShopKey(session);
  await withStore("debtPayments", "readwrite", (store) => {
    store.put({ ...payment, key: entityKey(shopKey, payment.id), shopKey });
  });
}

export async function saveWebStockMovement(session, movement) {
  const shopKey = getWebShopKey(session);
  const id = movement.id || movement.movementId;
  await withStore("stockMovements", "readwrite", (store) => {
    store.put({ ...movement, id, key: entityKey(shopKey, id), shopKey });
  });
}

async function markOperations(shopKey, updates) {
  await withStore("syncOperations", "readwrite", async (store) => {
    for (const update of updates) {
      const key = entityKey(shopKey, update.idempotencyKey);
      const existing = await requestToPromise(store.get(key));
      if (!existing) continue;
      store.put({ ...existing, ...update, key, shopKey, updatedAt: nowIso() });
    }
  });
}

async function markOperationsById(shopKey, updates) {
  await withStore("syncOperations", "readwrite", async (store) => {
    const operations = await requestToPromise(store.getAll());
    const operationById = new Map(operations.filter((operation) => operation.shopKey === shopKey).map((operation) => [operation.id, operation]));
    for (const update of updates) {
      const existing = operationById.get(update.id);
      if (!existing) continue;
      store.put({ ...existing, ...update, shopKey, updatedAt: nowIso() });
    }
  });
}

async function resetStaleProcessingOperations(shopKey) {
  const staleBefore = new Date(Date.now() - PROCESSING_TIMEOUT_MS).toISOString();
  await withStore("syncOperations", "readwrite", async (store) => {
    const operations = await requestToPromise(store.getAll());
    operations
      .filter((operation) => operation.shopKey === shopKey && operation.status === "PROCESSING" && String(operation.updatedAt || "") <= staleBefore)
      .forEach((operation) => {
        store.put({
          ...operation,
          lastError: SYNC_ERROR_CODES.recoveredInterruptedProcessingBatch,
          nextAttemptAt: null,
          status: "QUEUED",
          updatedAt: nowIso(),
        });
      });
  });
}

async function repairLegacySaleSyncPayloads(shopKey) {
  await withStore("syncOperations", "readwrite", async (store) => {
    const operations = await requestToPromise(store.getAll());
    operations
      .filter((operation) =>
        operation.shopKey === shopKey &&
        operation.operationType === "SALE_RECORDED" &&
        ["QUEUED", "FAILED"].includes(operation.status)
      )
      .forEach((operation) => {
        const payload = sanitizeWebSyncPayload(operation.operationType, operation.payload);
        if (JSON.stringify(payload) === JSON.stringify(operation.payload)) return;
        store.put({
          ...operation,
          lastError: null,
          nextAttemptAt: null,
          payload,
          status: "QUEUED",
          updatedAt: nowIso(),
        });
      });
  });
}

export async function processWebSyncQueue(session) {
  if (
    !session?.sessionId ||
    isSubscriptionReadOnly(session.subscription) ||
    (typeof navigator !== "undefined" && !navigator.onLine)
  ) {
    return loadWebOfflineWorkspace(session);
  }

  const shopKey = getWebShopKey(session);
  const lock = acquireSyncLock(shopKey);
  if (!lock.acquired) {
    return loadWebOfflineWorkspace(session);
  }

  try {
    await prepareWebWorkspaceForAuthGeneration(session);
    await resetStaleProcessingOperations(shopKey);
    await repairLegacySaleSyncPayloads(shopKey);
    const storedOperations = await getAll("syncOperations");
    const staleOperations = storedOperations
      .filter((operation) =>
        operation.shopKey === shopKey &&
        ["QUEUED", "PROCESSING", "FAILED"].includes(operation.status) &&
        (Number(operation.authGeneration || 0) !== Number(session.authGeneration || 0) ||
          operation.ownerUserId !== session.userId ||
          operation.branchId !== session.selectedBranchId)
      );
    if (staleOperations.length) {
      await markOperationsById(shopKey, staleOperations.map((operation) => ({
        id: operation.id,
        lastError: "Created under a replaced login session; review is required.",
        nextAttemptAt: null,
        status: "QUARANTINED",
      })));
    }
    const allOperations = (await getAll("syncOperations"))
      .filter((operation) =>
        operation.shopKey === shopKey &&
        operation.ownerUserId === session.userId &&
        operation.branchId === session.selectedBranchId &&
        ["QUEUED", "FAILED"].includes(operation.status))
      .sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
    const { deferred, selected } = selectDueOperationsForBatch(allOperations);
    if (deferred.length) {
      await markOperationsById(shopKey, deferred);
    }
    const operations = selected.slice(0, BATCH_SIZE);

    if (!operations.length) {
      try {
        await refreshWebOfflineWorkspace(session);
      } catch {
        // A pull failure should not block already cached workspace data.
      }
      const workspace = await loadWebOfflineWorkspace(session);
      await reportWebSyncHealth(session, workspace);
      return workspace;
    }

    await markOperations(shopKey, operations.map((operation) => ({
      attempts: Number(operation.attempts || 0) + 1,
      idempotencyKey: operation.idempotencyKey,
      lastError: null,
      nextAttemptAt: null,
      status: "PROCESSING",
    })));

    try {
      const clientBatchId = `web-batch-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const response = await syncOutboundOperationsBatch(session.sessionId, operations.map((operation) => ({
        aggregateId: operation.aggregateId,
        authGeneration: Number(operation.authGeneration || 0),
        branchId: operation.branchId,
        id: operation.id,
        idempotencyKey: operation.idempotencyKey,
        operationType: operation.operationType,
        payload: sanitizeWebSyncPayload(operation.operationType, operation.payload),
      })), clientBatchId);
      const results = response?.results ?? [];
      const syncedResultKeys = new Set(results.filter((result) => result.status === "SYNCED").map((result) => result.idempotencyKey));
      await markOperations(shopKey, results.map((result) => ({
        idempotencyKey: result.idempotencyKey,
        lastError: result.error ?? null,
        nextAttemptAt: result.status === "SYNCED" || result.errorCode === "STALE_SESSION_GENERATION"
          ? null
          : getBackoffDate((operations.find((operation) => operation.idempotencyKey === result.idempotencyKey)?.attempts ?? 0) + 1),
        status: result.status === "SYNCED"
          ? "SYNCED"
          : result.errorCode === "STALE_SESSION_GENERATION"
            ? "QUARANTINED"
            : "FAILED",
      })));
      await markSyncedLocalRecords(session, operations.filter((operation) => syncedResultKeys.has(operation.idempotencyKey)));
      await setMetadata(shopKey, "lastSyncedAt", nowIso());

      try {
        await refreshWebOfflineWorkspace(session);
      } catch {
        // A refresh pull failure should not undo the successful outbound sync result.
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Sync failed";
      await markOperations(shopKey, operations.map((operation) => ({
        idempotencyKey: operation.idempotencyKey,
        lastError: message,
        nextAttemptAt: error?.code === "SESSION_REPLACED" ? null : getBackoffDate(Number(operation.attempts || 0) + 1),
        status: error?.code === "SESSION_REPLACED" ? "QUARANTINED" : "FAILED",
      })));
    }

    const workspace = await loadWebOfflineWorkspace(session);
    await reportWebSyncHealth(session, workspace);
    return workspace;
  } finally {
    releaseSyncLock(lock);
  }
}

async function reportWebSyncHealth(session, workspace) {
  if (!session?.sessionId || !workspace?.sync || (typeof navigator !== "undefined" && !navigator.onLine)) {
    return;
  }
  try {
    const shopKey = getWebShopKey(session);
    const operations = (await getAll("syncOperations"))
      .filter((operation) => operation.shopKey === shopKey && ["QUEUED", "PROCESSING", "FAILED"].includes(operation.status));
    const operationCounts = operations.reduce((counts, operation) => ({
      ...counts,
      [operation.operationType]: (counts[operation.operationType] ?? 0) + 1,
    }), {});
    const oldestQueuedAt = operations
      .map((operation) => operation.createdAt)
      .filter(Boolean)
      .sort()[0] ?? null;
    const errorCode = classifyWebSyncError(workspace.sync.lastError);
    const telemetrySignature = JSON.stringify({
      errorCode,
      failedCount: workspace.sync.failedCount,
      operationCounts,
      processingCount: operations.filter((operation) => operation.status === "PROCESSING").length,
      queuedCount: workspace.sync.queuedCount,
    });
    if (
      telemetrySignature === lastTelemetrySignature &&
      Date.now() - lastTelemetryAt < TELEMETRY_MAX_INTERVAL_MS
    ) {
      return;
    }
    const telemetry = await reportSyncTelemetry(session.sessionId, {
      appVersion: process.env.REACT_APP_VERSION || "",
      errorCode,
      errorFingerprint: errorCode,
      failedCount: workspace.sync.failedCount,
      lastAttemptAt: new Date().toISOString(),
      lastSuccessAt: workspace.sync.lastSyncedAt,
      oldestQueuedAt,
      operationCounts,
      processingCount: operations.filter((operation) => operation.status === "PROCESSING").length,
      queuedCount: workspace.sync.queuedCount,
      schemaVersion: DB_VERSION,
      workerState: workspace.sync.failedCount
        ? "backoff"
        : workspace.sync.isProcessing
          ? "processing"
          : "idle",
    });
    lastTelemetryAt = Date.now();
    lastTelemetrySignature = telemetrySignature;
    for (const command of telemetry.commands ?? []) {
      await executeWebSyncCommand(session, command);
    }
  } catch {
    // Health reporting is best-effort and must never block the merchant workspace.
  }
}

async function executeWebSyncCommand(session, command) {
  return runWebSyncCommand({
    acknowledge: acknowledgeSyncCommand,
    command,
    refreshWorkspace: refreshWebOfflineWorkspace,
    retryRecoverable: retryRecoverableWebSyncOperations,
    session,
  });
}

async function refreshWebOfflineWorkspace(session) {
  const shopKey = getWebShopKey(session);
  let cursor = (await getMetadata(shopKey, "workspacePullCursor").catch(() => null))?.value || "";
  for (let page = 0; page < 1000; page += 1) {
    const pulled = await fetchWorkspaceSnapshot(session.sessionId, 60, cursor);
    if (!pulled?.workspace) {
      throw new Error("Workspace refresh returned no snapshot");
    }
    await seedWebOfflineWorkspace(session, {
      debtPayments: pulled.workspace.debtPayments ?? [],
      debts: pulled.workspace.debts ?? [],
      inventoryAllocations: pulled.workspace.inventoryAllocations ?? [],
      products: pulled.workspace.products ?? [],
      sales: pulled.workspace.sales ?? [],
      stockMovements: pulled.workspace.stockMovements ?? [],
    });
    cursor = pulled.workspace.nextCursor || cursor;
    await setMetadata(shopKey, "workspacePullCursor", cursor);
    if (!pulled.workspace.hasMore) {
      await maintainWebOfflineDatabase(session);
      return pulled.workspace;
    }
  }
  throw new Error("Workspace synchronization exceeded the safe pagination limit.");
}

async function retryRecoverableWebSyncOperations(session) {
  const shopKey = getWebShopKey(session);
  await withStore("syncOperations", "readwrite", async (store) => {
    const operations = await requestToPromise(store.getAll());
    operations
      .filter((operation) => operation.shopKey === shopKey && isWaitingOperation(operation))
      .forEach((operation) => {
        store.put({
          ...operation,
          lastError: null,
          nextAttemptAt: null,
          status: "QUEUED",
          updatedAt: nowIso(),
        });
      });
  });
}

function classifyWebSyncError(error) {
  if (!error) return "";
  const value = String(error).toUpperCase();
  if (value.includes("NETWORK") || value.includes("FETCH")) return "NETWORK_UNAVAILABLE";
  if (value.includes("WAIT_") || value.includes("WAITING FOR")) return "DEPENDENCY_WAIT";
  if (value.includes("CONFLICT") || value.includes("ALLOCATION")) return "INVENTORY_CONFLICT";
  if (value.includes("401") || value.includes("SESSION")) return "AUTHENTICATION_REQUIRED";
  if (value.includes("403") || value.includes("PERMISSION")) return "PERMISSION_DENIED";
  if (value.includes("400") || value.includes("422") || value.includes("INVALID")) return "VALIDATION_FAILED";
  if (value.includes("500") || value.includes("502") || value.includes("503")) return "SERVER_UNAVAILABLE";
  return "SYNC_FAILED";
}

function selectDueOperationsForBatch(operations) {
  const selected = [];
  const selectedIds = new Set();
  const deferred = [];
  const operationByProductUpsert = new Map();
  const operationBySale = new Map();
  const operationByStockProduct = new Map();

  for (const operation of operations) {
    if (operation.operationType === "PRODUCT_UPSERT") {
      operationByProductUpsert.set(operation.aggregateId, operation);
    }
    if (operation.operationType === "SALE_RECORDED") {
      operationBySale.set(operation.aggregateId, operation);
    }
    if (operation.operationType === "STOCK_MOVEMENT_RECORDED" && operation.payload?.productId && !operation.payload?.saleId && operation.payload?.type === "IN") {
      operationByStockProduct.set(operation.payload.productId, operation);
    }
  }

  const include = (operation) => {
    if (!operation || selectedIds.has(operation.id)) return;
    selected.push(operation);
    selectedIds.add(operation.id);
  };
  const wait = (operation, reason, dependency) => {
    if (!operation || selectedIds.has(operation.id)) return;
    deferred.push({
      id: operation.id,
      lastError: reason,
      nextAttemptAt: getDeferredRetryDate(dependency?.nextAttemptAt),
      status: "FAILED",
    });
  };
  const dependencyIsBlocked = (operation) => operation && operation.status === "FAILED" && !isDue(operation);

  for (const operation of operations) {
    if (!isDue(operation)) {
      continue;
    }

    if (operation.operationType === "SALE_RECORDED") {
      let blocked = false;
      for (const item of operation.payload?.items ?? []) {
        const productDependency = operationByProductUpsert.get(item.productId);
        const stockDependency = operationByStockProduct.get(item.productId);
        if (dependencyIsBlocked(productDependency)) {
          wait(operation, SYNC_ERROR_CODES.waitProductBeforeSale, productDependency);
          blocked = true;
          break;
        }
        if (dependencyIsBlocked(stockDependency)) {
          wait(operation, SYNC_ERROR_CODES.waitInitialStockBeforeSale, stockDependency);
          blocked = true;
          break;
        }
        include(productDependency);
        include(stockDependency);
      }
      if (blocked) {
        continue;
      }
      include(operation);
      continue;
    }

    if (operation.operationType === "STOCK_MOVEMENT_RECORDED") {
      const productDependency = operationByProductUpsert.get(operation.payload?.productId);
      if (dependencyIsBlocked(productDependency)) {
        wait(operation, SYNC_ERROR_CODES.waitProductBeforeStockMovement, productDependency);
        continue;
      }
      include(productDependency);
      if (operation.payload?.saleId) {
        const saleDependency = operationBySale.get(operation.payload.saleId);
        if (dependencyIsBlocked(saleDependency)) {
          wait(operation, SYNC_ERROR_CODES.waitSaleBeforeStockMovement, saleDependency);
          continue;
        }
        include(saleDependency);
      }
      include(operation);
      continue;
    }

    if (operation.operationType === "DEBT_RECORDED" && operation.payload?.saleId) {
      include(operationBySale.get(operation.payload.saleId));
      include(operation);
      continue;
    }

    include(operation);
  }

  return { deferred, selected };
}

async function markSyncedLocalRecords(session, operations) {
  const shopKey = getWebShopKey(session);
  await withStores(["sales"], "readwrite", async (stores) => {
    for (const operation of operations) {
      if (operation.operationType !== "SALE_RECORDED") continue;
      const saleKey = entityKey(shopKey, operation.aggregateId);
      const sale = await requestToPromise(stores.sales.get(saleKey));
      if (sale) {
        stores.sales.put({ ...sale, syncState: "Synced" });
      }
    }
  });
  await maintainWebOfflineDatabase(session);
}

async function maintainWebOfflineDatabase(session) {
  const shopKey = getWebShopKey(session);
  const now = nowIso();
  const detailedSaleCutoff = new Date(Date.now() - 730 * DAY_MS).toISOString();
  const movementCutoff = new Date(Date.now() - 90 * DAY_MS).toISOString();
  const receiptCutoff = new Date(Date.now() - 30 * DAY_MS).toISOString();
  await withStores(
    ["products", "sales", "salesArchive", "debts", "debtPayments", "stockMovements", "syncOperations"],
    "readwrite",
    async (stores) => {
      const [products, sales, debts, debtPayments, movements, operations] = await Promise.all([
        requestToPromise(stores.products.getAll()),
        requestToPromise(stores.sales.getAll()),
        requestToPromise(stores.debts.getAll()),
        requestToPromise(stores.debtPayments.getAll()),
        requestToPromise(stores.stockMovements.getAll()),
        requestToPromise(stores.syncOperations.getAll()),
      ]);
      const activeOperations = operations.filter(
        (operation) =>
          operation.shopKey === shopKey &&
          operation.status !== "SYNCED",
      );
      const activeAggregateIds = new Set(
        activeOperations.map((operation) => operation.aggregateId),
      );
      const activePayloadText = activeOperations
        .map((operation) => JSON.stringify(operation.payload || {}))
        .join("\n");
      const unresolvedSaleIds = new Set(
        debts
          .filter(
            (debt) =>
              debt.shopKey === shopKey &&
              debt.status !== "Paid" &&
              debt.saleId,
          )
          .map((debt) => debt.saleId),
      );
      const archivableSales = sales
        .filter(
          (sale) =>
            sale.shopKey === shopKey &&
            sale.syncState === "Synced" &&
            Boolean(sale.createdAtIso || sale.createdAt) &&
            String(sale.createdAtIso || sale.createdAt || "") < detailedSaleCutoff &&
            !activeAggregateIds.has(sale.id) &&
            !unresolvedSaleIds.has(sale.id),
        )
        .sort((left, right) =>
          String(left.createdAtIso || left.createdAt).localeCompare(
            String(right.createdAtIso || right.createdAt),
          ))
        .slice(0, 5000);
      const archiveByDay = new Map();
      for (const sale of archivableSales) {
        const day = String(sale.createdAtIso || sale.createdAt).slice(0, 10);
        const current = archiveByDay.get(day) || {
          amountPaid: 0,
          balanceDue: 0,
          count: 0,
          grossTotal: 0,
        };
        current.amountPaid += Number(sale.amountPaid || 0);
        current.balanceDue += Number(sale.balanceDue || 0);
        current.count += 1;
        current.grossTotal += Number(sale.total || 0);
        archiveByDay.set(day, current);
      }
      for (const [day, addition] of archiveByDay) {
        const key = entityKey(shopKey, day);
        const existing = await requestToPromise(stores.salesArchive.get(key));
        stores.salesArchive.put({
          amountPaid: Number(existing?.amountPaid || 0) + addition.amountPaid,
          balanceDue: Number(existing?.balanceDue || 0) + addition.balanceDue,
          count: Number(existing?.count || 0) + addition.count,
          day,
          grossTotal: Number(existing?.grossTotal || 0) + addition.grossTotal,
          key,
          shopKey,
          updatedAt: now,
        });
      }
      archivableSales.forEach((sale) => stores.sales.delete(sale.key));
      movements
        .filter(
          (movement) =>
            movement.shopKey === shopKey &&
            Boolean(movement.occurredAt || movement.createdAt) &&
            String(movement.occurredAt || movement.createdAt || "") < movementCutoff &&
            !activeAggregateIds.has(movement.id || movement.movementId) &&
            !activeAggregateIds.has(movement.saleId),
        )
        .slice(0, 5000)
        .forEach((movement) => stores.stockMovements.delete(movement.key));
      operations
        .filter(
          (operation) =>
            operation.shopKey === shopKey &&
            operation.status === "SYNCED" &&
            String(operation.updatedAt || "") < receiptCutoff,
        )
        .slice(0, 5000)
        .forEach((operation) => stores.syncOperations.delete(operation.key));
      products
        .filter(
          (product) =>
            product.shopKey === shopKey &&
            ["Archived", "Disabled"].includes(product.status),
        )
        .sort((left, right) =>
          String(right.updatedAt || "").localeCompare(String(left.updatedAt || "")),
        )
        .slice(2000, 7000)
        .filter(
          (product) =>
            !activeAggregateIds.has(product.id) &&
            !activePayloadText.includes(JSON.stringify(product.id)),
        )
        .forEach((product) => stores.products.delete(product.key));
      const removableDebtIds = new Set(
        debts
          .filter(
            (debt) =>
              debt.shopKey === shopKey &&
              debt.status === "Paid" &&
              Boolean(debt.updatedAt || debt.dueDateIso) &&
              String(debt.updatedAt || debt.dueDateIso) < detailedSaleCutoff &&
              !activeAggregateIds.has(debt.id) &&
              !activeAggregateIds.has(debt.saleId),
          )
          .slice(0, 5000)
          .map((debt) => debt.id),
      );
      debts
        .filter((debt) => removableDebtIds.has(debt.id))
        .forEach((debt) => stores.debts.delete(debt.key));
      debtPayments
        .filter(
          (payment) =>
            payment.shopKey === shopKey &&
            removableDebtIds.has(payment.debtId) &&
            !activeAggregateIds.has(payment.id),
        )
        .forEach((payment) => stores.debtPayments.delete(payment.key));
    },
  );
}
